/**
 * feat-242 — every `attention-*` class string in the curriculum editor, in one
 * module. Canonical token ruling: `.team/design/tokens/TOKENS.md` §1o.
 *
 * ── WHY THIS FILE EXISTS ──────────────────────────────────────────────────
 * The family had grown to 25 literal occurrences across four files. That is not
 * a tidiness problem, it is a correctness one: a rule that has to be re-obeyed
 * at 25 sites is a rule that will be broken at one of them. It was — twice, both
 * below — and fixing it by hand at every site is the argument for doing this
 * instead. With the constants, each fix was ONE edit.
 *
 * It sits at `tracks/` level rather than inside `map/` because both the Map and
 * the shipped `LessonLibraryPane` footer use the family. The Map is not its
 * owner, just its largest consumer.
 *
 * ── THE TWO RULES, AND THE ONE THAT WAS BROKEN ────────────────────────────
 *
 * **1. THE INK IS NEVER TRANSLUCENT.** An opacity modifier on an `-on` ink
 * composites it toward the fill and destroys the contrast the token was minted
 * to guarantee. Measured on `attention-surface`, by three independently written
 * implementations of the WCAG formula that agree to three decimals:
 *
 * | class | ratio | 11px floor | |
 * |---|---|---|---|
 * | `text-attention-on` | **5.440:1** | 4.5 | ✅ |
 * | `text-attention-on/85` | **3.980:1** | 4.5 | ❌ shipped once, corrected |
 * | `text-attention-on/90` | **4.418:1** | 4.5 | ❌ |
 * | `text-attention-on/95` | 4.885:1 | 4.5 | ✅ — and still forbidden, see below |
 *
 * Note the last row honestly: it is **not** true that every alpha fails. `/95`
 * passes *on this surface*. The rule is still "never", and the reason is the one
 * §1o actually gives — **an `-on` ink can only be guaranteed against an opaque
 * surface**, so any alpha makes the ratio a function of whatever happens to be
 * behind the element, and one class pair ends up with several ratios across the
 * panes it renders in. "Every alpha fails" would be a false premise supporting a
 * true conclusion, and those are worth more care than a wrong conclusion,
 * because nobody re-checks them.
 *
 * **To make a line read as secondary, change SIZE or WEIGHT, never alpha.** The
 * two corrected sites already carry `text-[11px]` regular against an
 * `text-[11.5px] font-bold` headline, so the hierarchy survived the fix
 * untouched.
 *
 * **2. THE FILL IS OPAQUE.** Never `bg-attention-surface/40` — the instance that
 * shipped happened to measure 5.762:1 because it sat over white, **and that is
 * exactly the trap**: it is not white on every pane.
 *
 * ── THE GOLD COLLISION IS RULED, NOT OPEN ─────────────────────────────────
 * `attention-on #805E08` and `feat-241`'s `dash-gold-on #8A5A11` are the closest
 * ink pair in the system (ΔE76 7.55) and mean opposite things. **Preston ruled it
 * 2026-08-16: ship both as-is** (`TOKENS.md` §1o, commit `bafedd2`). The three
 * rules below are the STANDING STATE, not an interim measure:
 *
 *   1. Neither family may be used for the other's meaning — `attention-*` never
 *      means progress; `dash-gold-on` never means a problem.
 *   2. Both are told apart by **glyph and copy, never hue alone**. Every consumer
 *      of these constants pairs them with a hazard glyph and a sentence.
 *   3. A third feature reaching for "dark gold on pale gold" must pick one of
 *      these two and say which. Minting a third is the failure the rule prevents.
 */

/** Mark fill + ink — the pair every hazard mark block uses. Opaque, both halves. */
export const ATTENTION_MARK = 'bg-attention-surface text-attention-on';

/** Ink alone: a mark's sub-lines, a marked chip's title, the library footer's
 *  split count. Never carries an opacity modifier. */
export const ATTENTION_INK = 'text-attention-on';

/** Opaque fill alone, for a container that supplies its own ink. */
export const ATTENTION_SURFACE = 'bg-attention-surface';

/** Node border / hairline — the graphic role, 3:1 floor, non-text. */
export const ATTENTION_BORDER = 'border-attention-line';

/**
 * feat-248 — the LEFT-EDGE variant, for a card whose whole left border is the
 * hazard stripe (the importer's needs-review card). It exists as a constant for
 * the same reason every other string here does: `border-l-attention-line` is a
 * different Tailwind class from `border-attention-line`, so a consumer needing
 * the left edge would otherwise hand-write it and the census would miss it.
 *
 * `attention-line #AE8213` and NOT `attention-on`: a 4px bar is a GRAPHIC, so
 * WCAG 1.4.11's 3:1 binds, not 4.5:1 — measured 3.499:1 on the white card.
 * `attention-on` also passes (5.962:1) and is still wrong here, on HIERARCHY:
 * an opaque near-brown 4px bar would outweigh the chip text it exists to
 * reinforce. §1o calls `attention-line` a "non-gating" stroke and the importer's
 * card IS gating — but the stripe is not what gates it (the flag, the chip and
 * the disabled Commit are). The stripe is reinforcement, which is the role this
 * token is for.
 */
export const ATTENTION_BORDER_LEFT = 'border-l-attention-line';

/** 1px outline for a marked chip. `outline` rather than `border` so adding the
 *  mark does not reflow the row it sits in. */
export const ATTENTION_OUTLINE = 'outline outline-1 outline-attention-line';

/**
 * feat-243 — the INVERTED pair: `attention-on` as a fill with white ink, for a
 * primary action sitting inside an `attention-*` container (the conflict
 * banner's "Re-apply my change"). White on `#805E08` measures **5.962:1**.
 *
 * It lives here rather than inline for rule 1's reason turned around: this is
 * the one place the ink and the fill swap roles, so it is the one place someone
 * would reach for `bg-attention-on/90` to "soften" the button — which composites
 * the fill toward whatever is behind it and takes the white text's contrast with
 * it. One constant, no opacity, nowhere to get it wrong.
 */
export const ATTENTION_BUTTON = 'bg-attention-on text-white';

/**
 * feat-243 — a chip or pill INSIDE an `attention-*` container: opaque white on
 * the pale-gold surface, hairline in the family. Same pair for both, because a
 * suppressed action's pill has to occupy the button's slot at the same geometry
 * — that is what makes the absence of a control read as a decision rather than
 * as a missing feature.
 */
export const ATTENTION_ON_SURFACE_CHIP = 'bg-white border border-attention-line';

/**
 * ⚠️ NEVER PUT A `fill-*` CLASS ON A CONNECTOR PATH. Use the `_LINE` pair below
 * for the line and the `_HEAD` pair for the arrowhead.
 *
 * `fill="none"` is an SVG **presentation attribute**, and a presentation
 * attribute sits at the BOTTOM of the cascade — any CSS declaration outranks it.
 * So `<path fill="none" className="… fill-attention-line">` renders FILLED: the
 * class wins, the browser implicitly closes the open cubic, and the connector
 * becomes a solid tapered wedge across the canvas.
 *
 * This shipped. It reached dev.bespokecast.ai and was caught by a human looking
 * at the page, because **every instrument in this repo is structurally blind to
 * it**: jsdom applies no stylesheet, so in tests the `fill="none"` attribute is
 * the only thing present and the path is correctly unfilled; AC6 parses
 * endpoints out of the `d` attribute, which fill cannot affect; and the class
 * census asserts class NAMES, which were correct the whole time. Compiling the
 * Tailwind config to confirm `fill-attention-line` is emitted — which QA did —
 * verifies the mechanism of the defect as working.
 *
 * The `_LINE` constants are the fix and they are not a style preference: they
 * are the only thing that keeps a stroke a stroke.
 */

/** Stroke ONLY — the non-gating (H3) connector line. No fill, ever. */
export const ATTENTION_EDGE_IGNORED_LINE = 'stroke-attention-line';

/** Stroke ONLY — the loop (H4) connector line. No fill, ever. */
export const ATTENTION_EDGE_LOOP_LINE = 'stroke-attention-on';

/** Stroke + fill for a non-gating (H3) ARROWHEAD — a closed triangle, which is
 *  the one element here that is supposed to be filled. */
export const ATTENTION_EDGE_IGNORED_HEAD = 'stroke-attention-line fill-attention-line';

/** Stroke + fill for a loop (H4) ARROWHEAD. */
export const ATTENTION_EDGE_LOOP_HEAD = 'stroke-attention-on fill-attention-on';
